import { useEffect, useMemo } from "react";

import { Button, ButtonIconWithLabel, Icon_Download24 } from "@shapr3d/frontend-lib";

import noThumbnail from "../../assets/images/no-thumbnail.png";
import { defaultPrivateSpaceName, defaultTeamSpaceName } from "../../config";
import useAccount from "../../hooks/useAccount";
import type { ProjectData } from "../../hooks/useProject";
import { handleFileDownload } from "../../utils/handleFileDownload";
import { formatDate } from "../../utils/helpers";
import useTracker from "../../utils/useTracker";
import { DeepLinkButton } from "../DeepLinkButton/DeepLinkButton";
import { UserName } from "../UserName/UserName";

import style from "./ProjectPageHeader.module.css";

export const ProjectPageHeader = ({
  projectData,
  isDownloadEnabled,
}: {
  projectData: ProjectData;
  isDownloadEnabled?: boolean;
}) => {
  const { data: accountData } = useAccount();
  const tracker = useTracker();

  const userName = useMemo(() => {
    const user = projectData.users.find(
      (user) => user.userId === projectData.latestRevision.uploadedByUserId
    );
    if (user && user.userId === accountData?.id) {
      return `${user.name} (You)`;
    }

    return user ? user.name : projectData.latestRevision.uploadedByUserId;
  }, [projectData, accountData]);

  const spaceName =
    projectData.space?.spaceName ||
    (projectData.space?.spaceType === "private" ? defaultPrivateSpaceName : "");

  useEffect(() => {
    if (isDownloadEnabled) {
      tracker.trackProjectPageDownloadFileShown({
        projectId: projectData.project.projectId,
        projectTeamId: projectData.teamId,
      });
    }
  }, [isDownloadEnabled, tracker, projectData.project.projectId, projectData.teamId]);

  return (
    <>
      <div className={style.headerMobile}>
        <img
          src={
            projectData?.latestRevision.thumbnailDownloads?.light?.url
              ? projectData?.latestRevision.thumbnailDownloads?.light?.url
              : noThumbnail
          }
        />

        <div className={style.headerMobileData}>
          <div>
            {spaceName && <div className={style.pageTitle}>{spaceName}</div>}
            <div className={style.headerTitle}>
              {projectData.project.name || defaultTeamSpaceName}
            </div>

            <div className={style.headerSubTitle}>
              <span style={{ marginRight: "4px" }}>
                Last edited: {formatDate(projectData.latestRevision.lastModifiedAt ?? Date())} ·
              </span>{" "}
              <UserName name={userName ?? "-"} />
            </div>
            {projectData?.latestRevision.fileDownload?.url && (
              <div className={style.ctaBoxTablet}>
                {isDownloadEnabled && (
                  <Button
                    variant="grayscale"
                    onClick={() => {
                      tracker.trackDownloadFileClicked({
                        projectTeamId: projectData.teamId,
                        projectId: projectData.project.projectId,
                      });
                      handleFileDownload(projectData?.latestRevision.fileDownload?.url);
                    }}
                  >
                    <ButtonIconWithLabel icon={<Icon_Download24 />} label="Save as .shapr file" />
                  </Button>
                )}
                <DeepLinkButton />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={style.wrapper}>
        <div className={style.header}>
          <div>
            <div className={style.pageTitle}>{spaceName}</div>
            <div className={style.headerTitle}>
              {projectData.project.name || defaultTeamSpaceName}
            </div>
            <div className={style.headerSubTitle}>
              <span style={{ marginRight: "4px" }}>
                Last edited: {formatDate(projectData.latestRevision.lastModifiedAt ?? Date())} ·
              </span>{" "}
              <UserName name={userName ?? ""} />
            </div>
            <div className={style.ctaBoxMobile}>
              {projectData?.latestRevision.fileDownload?.url && (
                <>
                  {isDownloadEnabled && (
                    <Button
                      variant="grayscale"
                      onClick={() => {
                        tracker.trackDownloadFileClicked({
                          projectTeamId: projectData.teamId,
                          projectId: projectData.project.projectId,
                        });
                        handleFileDownload(projectData?.latestRevision.fileDownload?.url);
                      }}
                    >
                      <ButtonIconWithLabel icon={<Icon_Download24 />} label="Save as .shapr file" />
                    </Button>
                  )}
                  <DeepLinkButton />
                </>
              )}
            </div>
          </div>

          {projectData?.latestRevision.fileDownload?.url && (
            <div className={style.buttons}>
              {isDownloadEnabled && (
                <Button
                  variant="grayscale"
                  onClick={() => {
                    tracker.trackDownloadFileClicked({
                      projectTeamId: projectData.teamId,
                      projectId: projectData.project.projectId,
                    });
                    handleFileDownload(projectData?.latestRevision.fileDownload?.url);
                  }}
                >
                  <ButtonIconWithLabel icon={<Icon_Download24 />} label="Save as .shapr file" />
                </Button>
              )}
              <DeepLinkButton />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
